import { Alert, Flex, Heading, InlineList, Text, View } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ExternalService } from "../../../../../../services/ExternalService";
import ChartColors from "../../../../../../utils/ChartColors";
import { sort } from "../../../../../../utils/utils";
import { getOriginalOutcomeVersion } from "../../../../../LearningOutcomes/utils";
import { drawGraph } from "./utils";

const AssessmentChartData = ({
  measure,
  fullOutcomes,
  triggerMap,
  selectedSubject,
  filters,
}) => {
  const { t } = useTranslation();
  const [loadingChart, setLoadingChart] = useState(true);
  const [error, setError] = useState(false);
  const [noData, setNoData] = useState(false);
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");

  useEffect(() => {
    const getGraphData = async () => {
      try {
        setLoadingChart(true);
        setTitle(
          filters.masteryResults === "true"
            ? t("assessment.masteryAvgCourse")
            : t("assessment.gradeAvgCourse")
        );
        const context = "canvas_course_embedded";
        const outcomeIds = fullOutcomes.map(
          (lo) => getOriginalOutcomeVersion(lo).id
        );
        const terms = measure.terms.map((term) => term.canvas_id);
        const results = await ExternalService.getAssessmentResults(
          [],
          filters.mode,
          filters.masteryResults,
          context,
          selectedSubject.title,
          terms,
          outcomeIds
        );

        if (results[0].length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
          results[0].forEach((term) => {
            for (const key in term) {
              if (key !== "name" && term[key] !== null) {
                term[key] = Number(term[key].toFixed(2));
              }
            }
          });
          setData(results[0]);
        }
      } catch {
        setError(true);
      } finally {
        setLoadingChart(false);
      }
    };
    if (selectedSubject && fullOutcomes) {
      getGraphData();
    }
  }, [measure, triggerMap, selectedSubject, fullOutcomes]);

  useEffect(() => {
    if (data) {
      const yAxisLabel =
        filters.masteryResults === "false"
          ? t("dashboard.avgAssignmentGrade")
          : t("dashboard.percentAchievedMastery");
      drawGraph("course-chart-" + measure.id, yAxisLabel, data);
    }
  }, [data, measure.id]);

  return loadingChart ? (
    <Flex.Item size="100%" padding="0 0 0 large">
      <Skeleton style={{ height: 300, borderRadius: 5 }} />
    </Flex.Item>
  ) : error ? (
    <Flex.Item size="100%" padding="0 0 0 large">
      <Alert variant="error">
        {t("notifications.general.fetchError", {
          obj: t("results.assessmentResults"),
        })}
      </Alert>
    </Flex.Item>
  ) : noData ? (
    <Flex.Item size="100%" padding="0 0 0 large">
      <Alert variant="info">{t("assessment.noResultsFound")}</Alert>
    </Flex.Item>
  ) : (
    <>
      <Flex.Item size="100%" padding="small 0 0 large">
        <View textAlign="center">
          <Heading level="h4">{title}</Heading>
          <div
            id={"course-chart-" + measure.id}
            style={{ margin: "10px 0 -20px 0", width: "100%" }}
          />
        </View>
      </Flex.Item>
      <Flex.Item size="100%" padding="0 0 medium xx-large">
        <View as="div" textAlign="center">
          <InlineList delimiter="pipe" isUnstyled>
            {sort(Object.keys(data[0]).filter((key) => key !== "name")).map(
              (category, index) => (
                <InlineList.Item key={index}>
                  <div
                    style={{
                      display: "inline-block",
                      height: 10,
                      width: 10,
                      marginRight: 8,
                      backgroundColor: ChartColors[index],
                    }}
                  />
                  <Text as="span" size="small">
                    {category}
                  </Text>
                </InlineList.Item>
              )
            )}
          </InlineList>
        </View>
      </Flex.Item>
    </>
  );
};

export default AssessmentChartData;
